import { EventDetailsForView, EventWithCustomField } from "@ac-expo/api-types";
import { SvgName } from "@ui/types/svg";
import { ReactNode, useEffect, useState } from "react";
import { cn } from "../../../utils";
import { Svg } from "../Svg";

const activeEventPanelValues = {
  information: "information",
  program: "program",
  exponents: "exponents",
} as const;
export type EventPanel = (typeof activeEventPanelValues)[keyof typeof activeEventPanelValues];

export const isEventPanel = (panel: string): panel is EventPanel =>
  Object.values(activeEventPanelValues).includes(panel as EventPanel);

export function EventViewPanelTab({
  activePanel,
  setActivePanel,
  panelName,
  panelTitle,
  panelIcon,
}: {
  activePanel: EventPanel;
  setActivePanel: (to: EventPanel) => void;
  panelName: EventPanel;
  panelTitle: string;
  panelIcon: SvgName;
}) {
  return (
    <div
      className={cn("flex grow cursor-pointer flex-col items-center px-2 py-1", {
        "border-b-2 border-blue-500": activePanel === panelName,
        "border-gray-400": activePanel !== panelName,
      })}
      onClick={() => setActivePanel(panelName)}
      onKeyDown={() => setActivePanel(panelName)}
      role="button"
      tabIndex={0}
    >
      <div
        className={cn("flex flex-col items-center gap-1 rounded-md px-2 py-1 backdrop-blur-sm", {
          "bg-white/40": activePanel === panelName,
          "bg-white/20": activePanel !== panelName,
        })}
      >
        <Svg name={panelIcon} alt={panelIcon} width={16} isColored={activePanel === panelName} />
        <p
          className={cn("", {
            "text-blue-500": activePanel === panelName,
            "text-gray-400": activePanel !== panelName,
          })}
        >
          {panelTitle}
        </p>
      </div>
    </div>
  );
}

type EventViewPanelsProps = {
  event: EventWithCustomField<EventDetailsForView>;
  panels: Record<EventPanel, ReactNode>;
  onPanelChange?: (panel: EventPanel) => void;
  activePanel?: EventPanel;
};

export function EventViewPanelsCard({
  event,
  panels,
  onPanelChange,
  activePanel,
}: EventViewPanelsProps) {
  const [currentPanel, setCurrentPanel] = useState<EventPanel>(activePanel ?? "information");

  useEffect(() => {
    if (activePanel !== undefined && activePanel !== currentPanel) {
      setCurrentPanel(activePanel);
    }
  }, [activePanel]);

  const changePanel = (to: EventPanel) => {
    if (onPanelChange) {
      onPanelChange(to);
    }
    setCurrentPanel(to);
  };

  return (
    <div className="w-full">
      <div className="flex px-2 pt-6 lg:px-6">
        <EventViewPanelTab
          activePanel={currentPanel}
          setActivePanel={changePanel}
          panelName="information"
          panelTitle="Informations"
          panelIcon="pushPin"
        />
        {event.isProgramOnline &&
          event.activities.length > 0 &&
          event.activities[0].schedules.length > 0 && (
            <EventViewPanelTab
              activePanel={currentPanel}
              setActivePanel={changePanel}
              panelName="program"
              panelTitle="Programme"
              panelIcon="newsPaper"
            />
          )}
        {event.eventStructures.length > 0 && (
          <EventViewPanelTab
            activePanel={currentPanel}
            setActivePanel={changePanel}
            panelName="exponents"
            panelTitle="Exposants"
            panelIcon="users"
          />
        )}
      </div>

      <div className="bg-[#FCFCFD] lg:border md:rounded-3xl overflow-hidden">
        <div>{panels[currentPanel]}</div>
      </div>
    </div>
  );
}
